import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Target, Dumbbell, Wrench, Trees, Scale, Stethoscope, Home as HomeIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { API, DALLAS_SKYLINE } from "@/lib/constants";
import NotFoundPage from "@/pages/NotFoundPage";

const DEFAULT_IMAGE = "https://customer-assets.emergentagent.com/job_e227742f-fa2d-44b4-a974-4045826f8a41/artifacts/epvouypm_DMN%20Logo%20Final%20A%20%28Light%29%20transparent.PNG";

const IndustryPage = () => {
  const { slug } = useParams();
  const [industry, setIndustry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchIndustry = async () => {
      try {
        const response = await axios.get(`${API}/industries/${slug}`);
        setIndustry(response.data);
        document.title = response.data.meta_title;
      } catch (error) {
        console.error("Error fetching industry:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchIndustry();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-[#00E676] animate-pulse">Loading...</div>
      </div>
    );
  }
  
  if (!industry || !industry.title) {
    return <NotFoundPage />;
  }

  const renderIcon = () => {
    switch(industry.icon) {
      case "Dumbbell": return <Dumbbell className="w-16 h-16" />;
      case "Wrench": return <Wrench className="w-16 h-16" />;
      case "Trees": return <Trees className="w-16 h-16" />;
      case "Scale": return <Scale className="w-16 h-16" />;
      case "Stethoscope": return <Stethoscope className="w-16 h-16" />;
      case "HomeIcon": return <HomeIcon className="w-16 h-16" />;
      default: return <Target className="w-16 h-16" />;
    }
  };

  const industryName = industry.title.replace("SEO & Marketing for ", "");
  const industryHeadline = industry.headline;
  const industryDesc = industry.description;

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="industry-page">
      <Helmet>
        <title>{industry.meta_title || `${industryName} SEO & Marketing Dallas | Digital Media Ninja`}</title>
        <meta name="description" content={industry.meta_description || industryDesc} />
        <link rel="canonical" href={`https://digitalmedianinja.com/industries/${slug}`} />
        <meta property="og:title" content={`${industryName} Marketing Dallas`} />
        <meta property="og:description" content={industryDesc} />
        <meta property="og:image" content={DEFAULT_IMAGE} />
        <meta property="og:url" content={`https://digitalmedianinja.com/industries/${slug}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${industryName} Marketing Dallas`} />
        <meta name="twitter:description" content={industryDesc} />
      </Helmet>

      {/* Hero Section */}
      <div className="container-custom mb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <nav className="text-sm text-gray-500">
              <Link to="/" className="hover:text-[#00E676]">Home</Link>
              {" / "}
              <span>Industries</span>
              {" / "}
              <span className="text-white">{industryName}</span>
            </nav>
            <div className="text-[#00E676]">{renderIcon()}</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold">{industryHeadline}</h1>
            <p className="text-xl text-gray-400">{industryDesc}</p>
            
            {/* Stats */}
            {industry.stats && <IndustryStats stats={industry.stats} />}

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact"><Button className="btn-ninja">Get Free Industry Audit</Button></Link>
              <Button className="btn-outline">View Case Studies</Button>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-white/10">
            <img src={DALLAS_SKYLINE} alt="Dallas" className="w-full h-96 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="text-white font-bold text-lg">{industryName} Marketing Experts</p>
              <p className="text-gray-400 text-sm">Dallas-Fort Worth specialists</p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-[#0A0A0A] py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold mb-4 text-center">Our {industryName} Marketing Services</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">Comprehensive digital marketing solutions tailored for {industryName.toLowerCase()} in Dallas.</p>
          <IndustryFeatures features={industry.features} />
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <IndustryBenefits benefits={industry.benefits} />
            <div className="glass rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6">Free Industry-Specific Audit</h3>
              <p className="text-gray-400 mb-6">Get a customized marketing audit for your {industryName.toLowerCase()} business.</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-[#00E676]" />Competitor analysis
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-[#00E676]" />Current ranking assessment
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-[#00E676]" />Custom strategy recommendations
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-[#00E676]" />ROI projections
                </li>
              </ul>
              <Link to="/contact"><Button className="btn-ninja w-full">Get Your Free Audit</Button></Link>
            </div>
          </div>
        </div>
      </div>

      {/* Industry FAQs */}
      <div className="bg-[#0A0A0A] py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold mb-12 text-center">{industryName} Marketing FAQs</h2>
          <IndustryFAQs faqs={industry.faqs} />
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-gradient-to-r from-[#00E676]/10 to-[#6366F1]/10">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Grow Your {industryName} Business?</h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">Join successful {industryName.toLowerCase()} businesses in Dallas that trust Digital Media Ninja.</p>
          <Link to="/contact"><Button className="btn-ninja px-12">Get Your Free Industry Audit</Button></Link>
        </div>
      </div>
    </div>
  );
};

// Separate components
const IndustryStats = ({ stats }) => (
  <div className="grid grid-cols-3 gap-4">
    {Object.entries(stats).map(([key, value]) => (
      <div key={key} className="text-center p-4 rounded-xl bg-white/5 border border-white/10">
        <p className="text-[#00E676] font-bold text-2xl">{value}</p>
        <p className="text-gray-500 text-xs capitalize">{key.replace(/_/g, " ")}</p>
      </div>
    ))}
  </div>
);

const IndustryFeatures = ({ features }) => (
  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
    {features.map((feature, i) => (
      <div key={i} className="flex items-start gap-4 p-6 rounded-xl bg-white/5 border border-white/10 hover:border-[#00E676]/30 transition-all">
        <CheckCircle2 className="w-6 h-6 text-[#00E676] shrink-0 mt-1" />
        <span className="text-gray-300">{feature}</span>
      </div>
    ))}
  </div>
);

const IndustryBenefits = ({ benefits }) => (
  <div>
    <h2 className="text-3xl font-bold mb-8">Results You Can Expect</h2>
    <div className="space-y-6">
      {benefits.map((benefit, i) => (
        <div key={i} className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-[#00E676]/20 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-5 h-5 text-[#00E676]" />
          </div>
          <p className="text-lg text-gray-200">{benefit}</p>
        </div>
      ))}
    </div>
  </div>
);

const IndustryFAQs = ({ faqs }) => (
  <div className="max-w-3xl mx-auto">
    <Accordion type="single" collapsible className="space-y-4">
      {faqs.map((faq, i) => (
        <AccordionItem key={i} value={`faq-${i}`} className="border border-white/10 rounded-xl px-6 data-[state=open]:bg-white/5">
          <AccordionTrigger className="text-left font-medium hover:text-[#00E676] py-5">{faq.question}</AccordionTrigger>
          <AccordionContent className="text-gray-400 pb-5">{faq.answer}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </div>
);

export default IndustryPage;
